import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import type { CourseCoach } from 'server/dbx/checkin-coaches';

type Props = {
  courseId: UUID;
  userId: UUID;
  studentName: string;
  coaches: CourseCoach[];
  /** The course default, which unassigned students count as. */
  defaultCoachId?: UUID;
  assignedCoachId?: UUID;
  onChange(assignedCoachId?: UUID): void;
};

/** "Barb (default)" when the course has a default coach, else "No coach". */
export function unassignedLabel(coaches: CourseCoach[], defaultCoachId?: UUID) {
  const fallback = coaches.find((c) => c.id === defaultCoachId);
  return fallback ? `${fallback.name} (default)` : 'No coach';
}

/** Picks one student's coach, saving as soon as it changes. */
export function CoachPicker(props: Props) {
  const [isSaving, setIsSaving] = useState(false);

  async function change(value: string) {
    const coachUserId = (value || undefined) as UUID | undefined;
    setIsSaving(true);
    try {
      await rpx.checkins.setStudentCoach({
        courseId: props.courseId,
        userIds: [props.userId],
        coachUserId,
      });
      props.onChange(coachUserId);
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <select
      class="ruz-input py-1 text-sm w-auto"
      aria-label={`Coach for ${props.studentName}`}
      value={props.assignedCoachId || ''}
      disabled={isSaving}
      onChange={(e) => change((e.target as HTMLSelectElement).value)}
    >
      <option value="">{unassignedLabel(props.coaches, props.defaultCoachId)}</option>
      {props.coaches.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}
